// Angular Core
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import { provideServiceWorker } from '@angular/service-worker';

// App Components & Modules
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { SharedModule } from '@app/shared';

// Environment
import { environment } from '@src/environments/environment';

// Firebase
import { provideFirebaseApp, getApp } from '@angular/fire/app';
import { initializeApp as initializeFirebaseApp, getApps } from 'firebase/app';
import { providePerformance } from '@angular/fire/performance';
import { initializePerformance } from 'firebase/performance';

// Loading Bar
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';

// Forms & UI
import { FormlyModule } from '@ngx-formly/core';
import { QuillModule } from 'ngx-quill';
import { ScriptLoaderModule } from 'ngx-script-loader';
import { HotkeyModule } from 'angular2-hotkeys';

// Stripe
import { provideNgxStripe } from 'ngx-stripe';

// Localization
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';

// Transloco
import { TranslocoService } from '@jsverse/transloco';
import { AppTranslocoModule } from './app/core/transloco/transloco.module';
import { PRELOAD_TRANSLATIONS_PROVIDER } from './app/core/transloco/preload-translations';

// Services & Repositories
import { ErrorInterceptor, TokenInterceptor } from './app/core/interceptors';
import { AppRepository } from './app/app.repository';
import { AuthService } from './app/core/services/auth.service';
import { AuthRepository } from './app/core/services/auth.repository';
import { PreloadAdminGuard } from './app/core/guards/preload-admin.guard';
import { OrganizationsService } from './app/pages/shell/admin/organizations/organizations.service';
import { OrganizationRepository } from './app/pages/shell/admin/organizations/organization.repository';
import { PropertyService } from './app/pages/shell/admin/property/property.service';
import { BuildingService } from './app/pages/shell/admin/building/building.service';
import { PmsIntegrationService } from './app/pages/shell/admin/integrations/pms/pms-integration.service';
import { AppLoadingService } from './app/core/services/app-loading.service';
import { provideRouter } from '@angular/router';

// CASL
import { AppAbility, defineAbilitiesFor } from './app/app.ability';
import { BookingRepository } from './app/pages/shell/admin/booking/booking.repository';
import { BookingService } from './app/pages/shell/admin/booking/booking.service';
import { TicketService } from './app/pages/shell/admin/ticket/ticket.service';
import { TicketRepository } from './app/pages/shell/admin/ticket/ticket.repository';
import { InboxService } from './app/pages/shell/admin/inbox/inbox.service';
import { InboxRepository } from './app/pages/shell/admin/inbox/inbox.repository';
import { EntitlementRepository } from './app/pages/shell/admin/entitlements/entitlement.repository';
import { EntitlementService } from './app/pages/shell/admin/entitlements/entitlement.service';

registerLocaleData(en);
registerLocaleData(es);

if (environment.production) {
  enableProdMode();
}

// AngularFire v21 initializes the Firebase app lazily (only when `FirebaseApp` is
// injected through DI). Our services use the raw Firebase SDK (getFirestore/getAuth/
// onAuthStateChanged), which bypasses DI, so the default app would never be created
// — yielding "No Firebase App '[DEFAULT]' has been created". Initialize it eagerly
// here so any raw SDK call has a default app. `provideFirebaseApp` below reuses it.
if (!getApps().length) {
  initializeFirebaseApp(environment.firebase);
}

bootstrapApplication(AppComponent, {
  providers: [
    // Router
    provideRouter(APP_ROUTES),

    // CDK 21 renders overlays with the native Popover API by default, which
    // puts every dialog/sheet/menu in the browser's TOP LAYER. The top layer
    // paints above ALL z-indexed content — including the sonner toaster
    // (z-index 999999999) — so toasts showed up dimmed/blurred behind any open
    // dialog's backdrop. Opting out restores classic z-index stacking, where
    // the toaster wins (the pre-NG21 behavior this app was designed around).
    // Revisit if a toast-in-top-layer strategy lands upstream in @spartan-ng/brain/sonner.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

    // Core Angular modules
    importProvidersFrom(
      BrowserAnimationsModule,
      SharedModule,
      FormsModule,
      ReactiveFormsModule,

      // NGX modules that need .forRoot()
      // NOTE: LoadingBarHttpClientModule is deliberately OUT: with realtime
      // presence heartbeats, triage counts and unread polling, an HTTP-driven
      // bar flashes constantly and signals nothing. The bar remains for
      // ROUTER transitions only (lazy-chunk loads), where progress is real.
      LoadingBarRouterModule,
      LoadingBarModule,
      FormlyModule.forRoot(),
      ScriptLoaderModule,
      HotkeyModule.forRoot(),
      QuillModule.forRoot({
        modules: {
          toolbar: [
            ['bold', 'italic'],
            ['blockquote'],
            [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
          ],
        },
      }),
      AppTranslocoModule,
    ),

    // Firebase — reuse the app initialized eagerly above (avoids double-init).
    // Auth is consumed via the raw SDK (getAuth()), not @angular/fire's `Auth`
    // DI (which is circular under Angular 21 + v1 RC), so no provideAuth here.
    provideFirebaseApp(() => getApp()),
    providePerformance(() =>
      initializePerformance(getApp(), {
        dataCollectionEnabled: true,
        instrumentationEnabled: true,
      }),
    ),

    // Stripe
    provideNgxStripe(environment.stripeKey),

    // HTTP client + DI-based interceptors. `withInterceptorsFromDi()` resolves
    // interceptors lazily per-request instead of at HttpClient construction,
    // which breaks the cycle HttpClient -> HTTP_INTERCEPTORS -> TokenInterceptor
    // -> AuthService -> HttpClient (NG0200 under Angular 21).
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },

    // CASL
    { provide: AppAbility, useFactory: defineAbilitiesFor },

    // Preload all translations
    PRELOAD_TRANSLATIONS_PROVIDER,

    // App services
    AppRepository,
    AuthRepository,
    AuthService,
    PreloadAdminGuard,
    BookingRepository,
    BookingService,
    TicketService,
    TicketRepository,
    OrganizationsService,
    OrganizationRepository,
    EntitlementService,
    EntitlementRepository,
    PropertyService,
    BuildingService,
    PmsIntegrationService,
    AppLoadingService,
    InboxService,
    InboxRepository,

    // PWA: opt in per environment file (see `pwaEnabled` in environments/*)
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.pwaEnabled,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
}).catch((err) => console.error(err));
